//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import {
  ILike2BuyV2ItemsSearchParams,
  ILike2BuyV2ItemsSearchResponse,
  ILike2BuyV2ItemsSearchResult,
  IAddLike2BuyV2ItemsResponse,
  IGetPinnedLike2BuyV2ItemsResponse,
  ILike2BuyV2PinItemsRequestBody,
  IArchiveLike2Buyv2ItemsRequestBody,
  IAddLike2BuyV2ItemsRequestBody,
  ILike2BuyV2MaterializeItemsRequestBody,
  ILike2BuyV2MaterializeItemsResponse,
  IMediaItemWithLike2BuyV2Status,
} from '@apis/like2buy-v2/like2BuyV2ModerationModel';
import { deserializeMediaItem } from '@apis/media-library/mediaLibraryApiUtils';
import Like2BuyV2StoreId from '@models/Like2BuyV2StoreId';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

function like2BuyV2ModerationFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch(
    'like2buy_gateway',
    `/api/v2/moderation${endpoint}`,
    init,
  );
}

/**
 *
 * Searches for Like2BuyV2 items for the given storeId
 *
 * GET /api/v2/moderation/:storeId/items
 */
export async function getLike2BuyV2Items(
  storeId: Like2BuyV2StoreId,
  req: ILike2BuyV2ItemsSearchParams,
  init?: RequestInit,
): Promise<ILike2BuyV2ItemsSearchResult> {
  const params = makeParams(req);
  const res = await like2BuyV2ModerationFetch(
    `/${storeId}/items?${params}`,
    init,
  );

  const like2BuyV2ItemsResponse =
    await res.json<ILike2BuyV2ItemsSearchResponse>((res) => ({
      data: {
        mediaItems: res.data.mediaItems.map<IMediaItemWithLike2BuyV2Status>(
          ({ item, status }) => {
            return {
              item: deserializeMediaItem(item),
              status,
            };
          },
        ),
        totalCount: res.data.totalCount,
      },
    }));

  return like2BuyV2ItemsResponse.data;
}

/**
 *
 * Gets materialized Like2BuyV2 items for specific cuids
 *
 * POST /api/v2/moderation/:storeId/items/materialize
 */
export async function materializeLike2BuyV2ItemsByCuid(
  storeId: Like2BuyV2StoreId,
  body: ILike2BuyV2MaterializeItemsRequestBody,
): Promise<IMediaItemWithLike2BuyV2Status[]> {
  const res = await like2BuyV2ModerationFetch(
    `/${storeId}/items/materialized`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );

  const like2BuyV2ItemsResponse =
    await res.json<ILike2BuyV2MaterializeItemsResponse>((res) => ({
      data: {
        items: res.data.items.map<IMediaItemWithLike2BuyV2Status>(
          ({ item, status }) => {
            return {
              item: deserializeMediaItem(item),
              status,
            };
          },
        ),
      },
    }));

  return like2BuyV2ItemsResponse.data.items;
}

/**
 *
 * Adds items to Like2BuyV2 Moderation for the given storeId
 *
 * POST /api/v2/moderation/:storeId/items
 */
export async function addLike2BuyV2Items(
  storeId: Like2BuyV2StoreId,
  body: IAddLike2BuyV2ItemsRequestBody,
): Promise<{ alreadyAddedItems: IMediaItemWithLike2BuyV2Status[] }> {
  const res = await like2BuyV2ModerationFetch(`/${storeId}/items`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

  const addItemsResponse = await res.json<IAddLike2BuyV2ItemsResponse>(
    (res) => ({
      data: {
        alreadyAddedItems:
          res.data.alreadyAddedItems.map<IMediaItemWithLike2BuyV2Status>(
            ({ item, status }) => {
              return {
                item: deserializeMediaItem(item),
                status,
              };
            },
          ),
      },
    }),
  );

  return addItemsResponse.data;
}

/**
 *
 * Archives items in Like2BuyV2 Moderation for the given storeId
 *
 * POST /api/v2/moderation/:storeId/items/archive
 */
export async function archiveLike2BuyV2Items(
  storeId: Like2BuyV2StoreId,
  body: IArchiveLike2Buyv2ItemsRequestBody,
): Promise<void> {
  await like2BuyV2ModerationFetch(`/${storeId}/items/archive`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 *
 * Gets the pinned Like2BuyV2 items for the given storeId
 *
 * GET /api/v2/moderation/:storeId/items/pins
 */
export async function getPinnedLike2BuyV2Items(
  storeId: Like2BuyV2StoreId,
): Promise<IMediaItemWithLike2BuyV2Status[]> {
  const res = await like2BuyV2ModerationFetch(`/${storeId}/items/pins`);
  const pinnedItemsResponse = await res.json<IGetPinnedLike2BuyV2ItemsResponse>(
    (res) => ({
      data: {
        pinnedItems: res.data.pinnedItems.map<IMediaItemWithLike2BuyV2Status>(
          ({ item, status }) => {
            return {
              item: deserializeMediaItem(item),
              status,
            };
          },
        ),
      },
    }),
  );

  return pinnedItemsResponse.data.pinnedItems;
}

/**
 *
 * Sets the pinned Like2BuyV2 items for the given storeId
 *
 * POST /api/v2/moderation/:storeId/items/pins
 */
export async function setPinnedLike2BuyV2Items(
  storeId: Like2BuyV2StoreId,
  body: ILike2BuyV2PinItemsRequestBody,
): Promise<void> {
  await like2BuyV2ModerationFetch(`/${storeId}/items/pins`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 *
 * Clears the Like2Buy V2 item cache for the given storeId
 *
 * DELETE /api/v2/moderation/:storeId/items/cache
 */
export async function clearLike2BuyV2ItemCache(
  storeId: Like2BuyV2StoreId,
): Promise<void> {
  await like2BuyV2ModerationFetch(`/${storeId}/items/cache`, {
    method: 'DELETE',
  });
}
